/**
 * ModeSelector — Pre-interview configuration screen.
 *
 * Displayed when a session is PENDING. The interviewee selects:
 * 1. Interview mode: Live Voice or Chat Interview
 * 2. Language: German or English
 * 3. (Chat only) Coordinator model: Opus or Sonnet
 *
 * Once confirmed, proceeds to the appropriate interview flow.
 */

import { useState } from 'react';
import { InterviewMode } from '@shared/types.ts';
import { cn } from '@/lib/utils';

interface ModeSelectorProps {
  sessionName: string;
  intervieweeName: string;
  structureName?: string;
  /** Called when the user selects Live Voice and clicks Start */
  onStartVoice: () => void;
  /** Called when the user selects Chat and clicks Start */
  onStartChat: (options: {
    language: string;
    coordinatorModel?: string;
  }) => void;
  /** Whether the start action is in progress */
  isStarting: boolean;
  /** Error message to display */
  error?: string | null;
}

const LANGUAGES = [
  { code: 'de', label: 'Deutsch (German)' },
  { code: 'en', label: 'English' },
] as const;

const COORDINATOR_MODELS = [
  { id: 'claude-sonnet-4-6', label: 'Claude Sonnet', description: 'Balanced quality and speed', disabled: false },
  { id: 'claude-opus-4-6', label: 'Claude Opus', description: 'Maximum question quality', disabled: true },
] as const;

export function ModeSelector({
  sessionName,
  intervieweeName,
  structureName,
  onStartVoice,
  onStartChat,
  isStarting,
  error,
}: ModeSelectorProps) {
  const [selectedMode, setSelectedMode] = useState<InterviewMode>(
    InterviewMode.ASYNC_CHAT
  );
  const [language, setLanguage] = useState('de');
  const [coordinatorModel, setCoordinatorModel] = useState<string>(
    COORDINATOR_MODELS[0].id
  );

  const handleStart = () => {
    if (isStarting) return;

    if (selectedMode === InterviewMode.LIVE_VOICE) {
      onStartVoice();
    } else {
      onStartChat({
        language,
        coordinatorModel,
      });
    }
  };

  return (
    <section className="mx-auto w-full max-w-2xl px-4 py-8 sm:px-6">
      <div className="rounded-2xl border border-border bg-card p-6 shadow-lg sm:p-8">
        {/* Header */}
        <div className="mb-6 text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-kaiva-coral text-lg font-bold text-white">
            K
          </div>
          <h1 className="text-lg font-semibold text-foreground sm:text-xl">
            {sessionName}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Welcome, {intervieweeName}
          </p>
          {structureName && (
            <p className="mt-0.5 text-xs text-muted-foreground">
              {structureName}
            </p>
          )}
        </div>

        {/* Mode selection */}
        <fieldset className="mb-6">
          <legend className="mb-3 text-sm font-semibold text-foreground">
            Choose your interview mode
          </legend>
          <div className="grid gap-3 sm:grid-cols-2">
            {/* Live Voice Card — disabled */}
            <div
              className="cursor-not-allowed rounded-xl border-2 border-border bg-muted/40 p-4 text-left opacity-50"
              aria-disabled="true"
            >
              <div className="mb-2 flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5 text-muted-foreground">
                  <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z" />
                  <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z" />
                </svg>
                <span className="text-sm font-semibold text-muted-foreground">
                  Live Voice Interview
                </span>
                <span className="ml-auto rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                  Coming soon
                </span>
              </div>
              <p className="text-xs leading-relaxed text-muted-foreground">
                Real-time voice conversation with the AI interviewer. Natural
                back-and-forth dialogue.
              </p>
            </div>

            {/* Chat Interview Card */}
            <button
              type="button"
              onClick={() => setSelectedMode(InterviewMode.ASYNC_CHAT)}
              className={cn(
                'rounded-xl border-2 p-4 text-left transition',
                selectedMode === InterviewMode.ASYNC_CHAT
                  ? 'border-kaiva-coral bg-kaiva-coral/5'
                  : 'border-border bg-background hover:border-muted-foreground/30'
              )}
            >
              <div className="mb-2 flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5 text-kaiva-coral">
                  <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z" />
                </svg>
                <span className="text-sm font-semibold text-foreground">
                  Interactive Canvas Interview
                </span>
              </div>
              <p className="text-xs leading-relaxed text-muted-foreground">
                Text-based interview at your own pace. Take time to think,
                type or use voice-to-text. Questions are read aloud.
              </p>
            </button>
          </div>
        </fieldset>

        {/* Language selection — shown for both modes */}
        <fieldset className="mb-6">
          <legend className="mb-2 text-sm font-semibold text-foreground">
            Interview language
          </legend>
          <select
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-kaiva-coral/40"
            aria-label="Select interview language"
          >
            {LANGUAGES.map((lang) => (
              <option key={lang.code} value={lang.code}>
                {lang.label}
              </option>
            ))}
          </select>
        </fieldset>

        {/* Coordinator model — only for Chat mode */}
        {selectedMode === InterviewMode.ASYNC_CHAT && (
          <fieldset className="mb-6">
            <legend className="mb-2 text-sm font-semibold text-foreground">
              AI model quality
            </legend>
            <div className="space-y-2">
              {COORDINATOR_MODELS.map((model) => (
                <label
                  key={model.id}
                  className={cn(
                    'flex items-start gap-3 rounded-lg border p-3 transition',
                    model.disabled
                      ? 'cursor-not-allowed border-border bg-muted/40 opacity-50'
                      : coordinatorModel === model.id
                        ? 'cursor-pointer border-kaiva-coral bg-kaiva-coral/5'
                        : 'cursor-pointer border-border bg-background hover:border-muted-foreground/30'
                  )}
                >
                  <input
                    type="radio"
                    name="coordinatorModel"
                    value={model.id}
                    checked={coordinatorModel === model.id}
                    onChange={(e) => !model.disabled && setCoordinatorModel(e.target.value)}
                    disabled={model.disabled}
                    className="mt-0.5 accent-[hsl(var(--kaiva-coral))]"
                  />
                  <div>
                    <p className={cn('text-sm font-medium', model.disabled ? 'text-muted-foreground' : 'text-foreground')}>
                      {model.label}
                      {model.disabled && (
                        <span className="ml-2 rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                          Coming soon
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {model.description}
                    </p>
                  </div>
                </label>
              ))}
            </div>
          </fieldset>
        )}

        {/* Error display */}
        {error && (
          <div className="mb-4 rounded-lg border border-kaiva-coral/30 bg-kaiva-coral/10 px-4 py-2 text-sm text-kaiva-coral">
            {error}
          </div>
        )}

        {/* Start button */}
        <button
          type="button"
          onClick={handleStart}
          disabled={isStarting}
          className={cn(
            'w-full rounded-xl py-3 text-sm font-semibold text-white transition',
            isStarting
              ? 'cursor-not-allowed bg-kaiva-coral/60'
              : 'bg-kaiva-coral hover:opacity-90'
          )}
        >
          {isStarting ? (
            <span className="flex items-center justify-center gap-2">
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
              Starting interview...
            </span>
          ) : selectedMode === InterviewMode.LIVE_VOICE ? (
            'Start Live Voice Interview'
          ) : (
            'Start Interactive Canvas Interview'
          )}
        </button>

        {/* Mode info note */}
        <p className="mt-3 text-center text-[11px] text-muted-foreground">
          {'No microphone required. You can type or optionally use voice-to-text.'}
        </p>
      </div>
    </section>
  );
}
